import React, { useEffect, useState, useMemo } from 'react';
import { Bell, Plus, Trash2, Calendar, Megaphone, X, AlertTriangle, Info, PartyPopper, Paperclip, BarChart2, Loader2, Pencil, Eye, EyeOff, CheckCircle, Clock } from 'lucide-react';
import { useI18n } from '@/shared/i18n';
import { useServices, useDeleteConfirmation, useToast, useFileViewer, useSection } from '@/app/providers';
import { Announcement, AnnouncementType } from '@/shared/types';
import { Button } from '../atoms/Button';
import { Input } from '../atoms/Input';
import { useCrudNotifications, usePaginatedList, useFileUpload, useDateFormatter } from '@/shared/hooks';
import { DataTable, RowActionsMenu, type ColumnDef } from '@/core/ui/components';
import { useAbility } from '@/modules/identity/application/ability';
import { SectionSelector } from '@/modules/sections/ui/components/SectionSelector';
import { SectionMultiSelect } from '@/modules/sections/ui/components/SectionMultiSelect';

// Helper para detectar mime type desde URL
const getMimeTypeFromUrl = (url: string): string => {
  const ext = url.split('.').pop()?.toLowerCase();
  const mimeTypes: Record<string, string> = {
    'pdf': 'application/pdf',
    'jpg': 'image/jpeg',
    'jpeg': 'image/jpeg',
    'png': 'image/png',
    'gif': 'image/gif',
    'webp': 'image/webp',
    'mp4': 'video/mp4',
    'webm': 'video/webm',
  };
  return mimeTypes[ext || ''] || 'application/octet-stream';
};

export const AdminAnnouncementList = () => {
  const { t } = useI18n();
  const { formatDate } = useDateFormatter();
  const ability = useAbility();
  const toast = useToast();
  const { confirmDelete } = useDeleteConfirmation();
  const { announcementRepo } = useServices();
  const { selectedSectionId, hasSections } = useSection();
  const { openFile } = useFileViewer();
  const { withCreateNotification, withDeleteNotification, withUpdateNotification } = useCrudNotifications({
    entityName: 'Comunicado',
  });
  const canCreateAnnouncement = ability.can('manage', 'all') || ability.can('create', 'Announcement');
  const canUpdateAnnouncement = ability.can('manage', 'all') || ability.can('update', 'Announcement');
  const canDeleteAnnouncement = ability.can('manage', 'all') || ability.can('delete', 'Announcement');

  const {
    data: announcements,
    meta,
    loading,
    setPage,
    pageSize,
    setPageSize,
    search,
    setSearch,
    filters,
    setFilters,
    refresh,
  } = usePaginatedList({
    fetchFn: (params) => announcementRepo.getAnnouncements({
      ...params,
      sectionId:
        typeof params.sectionId === 'string' && params.sectionId ? params.sectionId : undefined,
    }),
    initialFilters: { sectionId: selectedSectionId ?? '' },
    errorTitle: 'Error al cargar comunicados',
  });

  // La lista sigue al selector de sección del contexto, también cuando este se
  // resuelve después del montaje (sección guardada al recargar). El `fetchFn`
  // no cae al contexto: el filtro explícito ('' = todas) siempre gana.
  useEffect(() => {
    const sectionId = selectedSectionId ?? '';
    if (filters.sectionId !== sectionId) setFilters({ sectionId });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSectionId]);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [previewAnnouncement, setPreviewAnnouncement] = useState<Announcement | null>(null);
  const [isPublishing, setIsPublishing] = useState<string | null>(null);

  // File upload hook
  const { upload, isUploading, uploadedFile, reset: resetUpload } = useFileUpload('announcements');

  // Form State
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [type, setType] = useState<AnnouncementType>(AnnouncementType.INFO);
  const [expiresAt, setExpiresAt] = useState('');
  const [attachmentUrl, setAttachmentUrl] = useState('');
  const [sectionIds, setSectionIds] = useState<string[]>([]);

  const resetForm = () => {
    setTitle('');
    setContent('');
    setType(AnnouncementType.INFO);
    setExpiresAt('');
    setAttachmentUrl('');
    setSectionIds([]);
    resetUpload();
    setEditingId(null);
    setIsEditMode(false);
  };

  const openCreateModal = () => {
    if (!canCreateAnnouncement) return;
    resetForm();
    setIsModalOpen(true);
  };

  const openEditModal = (ann: Announcement) => {
    if (!canUpdateAnnouncement) return;
    setTitle(ann.title);
    setContent(ann.content);
    setType(ann.type);
    setExpiresAt(ann.expiresAt || '');
    setAttachmentUrl(ann.attachmentUrl || '');
    setSectionIds(ann.sectionIds ?? []);
    setEditingId(ann.id);
    setIsEditMode(true);
    setIsModalOpen(true);
  };

  const openPreview = (ann: Announcement) => {
    setPreviewAnnouncement(ann);
    setIsPreviewOpen(true);
  };

  const handleDelete = async (id: string, announcementTitle: string) => {
    if (!canDeleteAnnouncement) return;
    await confirmDelete({
      itemName: announcementTitle,
      itemType: 'Comunicado',
      onConfirm: async () => {
        await withDeleteNotification(() => announcementRepo.deleteAnnouncement(id));
        refresh();
      }
    });
  };

  const handleTogglePublish = async (ann: Announcement) => {
    if (!canUpdateAnnouncement) return;
    setIsPublishing(ann.id);
    try {
      if (ann.isPublished) {
        await announcementRepo.unpublishAnnouncement(ann.id);
        toast.success('Comunicado despublicado');
      } else {
        await announcementRepo.publishAnnouncement(ann.id);
        toast.success('Comunicado publicado');
      }
      refresh();
    } catch {
      toast.error('Error al cambiar estado del comunicado');
    } finally {
      setIsPublishing(null);
    }
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!title || !content) return;
    if (isEditMode && !canUpdateAnnouncement) return;
    if (!isEditMode && !canCreateAnnouncement) return;

    try {
      const payload: Omit<Announcement, 'id' | 'date'> = {
        title,
        content,
        type,
        expiresAt: expiresAt || undefined,
        attachmentUrl: attachmentUrl || undefined,
        attachmentFileId: uploadedFile?.id,
        sectionIds,
      };

      if (isEditMode && editingId) {
        await withUpdateNotification(() => announcementRepo.updateAnnouncement(editingId, payload));
      } else {
        await withCreateNotification(() => announcementRepo.createAnnouncement(payload));
      }

      setIsModalOpen(false);
      resetForm();
      refresh();
    } catch {
      // Error ya mostrado por el hook
    }
  };

  const getPriorityBadge = (p: AnnouncementType) => {
    switch (p) {
      case AnnouncementType.URGENT:
        return <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-bold bg-red-100 text-red-700 border border-red-200"><AlertTriangle size={12}/> {t('admin.ann.priority.urgent')}</span>;
      case AnnouncementType.IMPORTANT:
        return <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-bold bg-amber-100 text-amber-700 border border-amber-200"><Bell size={12}/> {t('admin.ann.priority.important')}</span>;
      case AnnouncementType.EVENT:
        return <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-bold bg-emerald-100 text-emerald-700 border border-emerald-200"><PartyPopper size={12}/> {t('admin.ann.priority.event')}</span>;
      default:
        return <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-bold bg-blue-50 text-blue-700 border border-blue-200"><Info size={12}/> {t('admin.ann.priority.info')}</span>;
    }
  };

  const getStatusBadge = (isPublished?: boolean) => {
    if (isPublished) {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">
          <CheckCircle size={12} /> Publicado
        </span>
      );
    }
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-600">
        <Clock size={12} /> Borrador
      </span>
    );
  };

  const columns: ColumnDef<Announcement>[] = useMemo(() => [
    {
      id: 'title',
      header: t('admin.ann.modal.title_field'),
      cell: (ann) => (
        <div>
          <p className="font-bold text-slate-800">{ann.title}</p>
          <p className="text-xs text-slate-500 line-clamp-2 max-w-xs">{ann.content}</p>
        </div>
      ),
    },
    {
      id: 'type',
      header: t('admin.ann.modal.priority'),
      cell: (ann) => getPriorityBadge(ann.type),
    },
    {
      id: 'status',
      header: 'Estado',
      cell: (ann) => getStatusBadge(ann.isPublished),
    },
    {
      id: 'date',
      header: 'Fecha',
      cell: (ann) => (
        <span className="flex items-center gap-1 text-slate-600 text-sm">
          <Calendar size={12} /> {formatDate(ann.date)}
        </span>
      ),
    },
    {
      id: 'expires',
      header: 'Expira',
      cell: (ann) => ann.expiresAt ? (
        <span className="text-amber-600 text-sm">{formatDate(ann.expiresAt)}</span>
      ) : <span className="text-slate-400 text-sm">-</span>,
    },
    {
      id: 'extras',
      header: 'Extras',
      cell: (ann) => (
        <div className="flex items-center gap-2">
          {ann.attachmentUrl && (
            <span className="flex items-center gap-1 text-xs text-slate-500">
              <Paperclip size={12} />
            </span>
          )}
          {ann.poll && (
            <span className="flex items-center gap-1 text-xs text-primary-600">
              <BarChart2 size={12} />
            </span>
          )}
          {!ann.attachmentUrl && !ann.poll && <span className="text-slate-400 text-sm">-</span>}
        </div>
      ),
    },
  ], [t, formatDate]);

  return (
    <>
      <DataTable
        data={announcements}
        columns={columns}
        keyExtractor={(ann) => ann.id}
        meta={meta}
        onPageChange={setPage}
        pageSize={pageSize}
        onPageSizeChange={setPageSize}
        title={t('admin.ann.title')}
        searchValue={search}
        onSearchChange={setSearch}
        searchPlaceholder="Buscar comunicado..."
        loading={loading}
        filters={
          hasSections && (
            <SectionSelector
              variant="slate"
              className="w-48"
              onSelectionChange={(id) => setFilters({ sectionId: id ?? '' })}
            />
          )
        }
        emptyMessage="No hay comunicados publicados."
        actions={
          canCreateAnnouncement ? (
            <Button onClick={openCreateModal} icon={<Plus size={16}/>}>
              {t('admin.ann.create')}
            </Button>
          ) : null
        }
        rowActions={(ann) => (
          <RowActionsMenu>
            {(close) => (
              <>
                <button
                  onClick={() => { openPreview(ann); close(); }}
                  className="w-full flex items-center gap-2 px-4 py-2 text-sm text-slate-700 hover:bg-slate-50 transition-colors"
                >
                  <Eye size={16} className="text-blue-500" /> Ver detalle
                </button>
                {canUpdateAnnouncement && (
                  <>
                    <button
                      onClick={() => { openEditModal(ann); close(); }}
                      className="w-full flex items-center gap-2 px-4 py-2 text-sm text-slate-700 hover:bg-slate-50 transition-colors"
                    >
                      <Pencil size={16} className="text-amber-500" /> Editar
                    </button>
                    <button
                      onClick={() => { handleTogglePublish(ann); close(); }}
                      disabled={isPublishing === ann.id}
                      className="w-full flex items-center gap-2 px-4 py-2 text-sm text-slate-700 hover:bg-slate-50 transition-colors disabled:opacity-50"
                    >
                      {isPublishing === ann.id ? (
                        <Loader2 size={16} className="animate-spin text-slate-400" />
                      ) : ann.isPublished ? (
                        <EyeOff size={16} className="text-slate-500" />
                      ) : (
                        <CheckCircle size={16} className="text-green-500" />
                      )}
                      {ann.isPublished ? 'Despublicar' : 'Publicar'}
                    </button>
                  </>
                )}
                {canDeleteAnnouncement && (
                  <>
                    <hr className="my-1 border-slate-100" />
                    <button
                      onClick={() => { handleDelete(ann.id, ann.title); close(); }}
                      className="w-full flex items-center gap-2 px-4 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors"
                    >
                      <Trash2 size={16} /> Eliminar
                    </button>
                  </>
                )}
              </>
            )}
          </RowActionsMenu>
        )}
      />

      {/* Modal de Crear/Editar */}
      {isModalOpen && ((isEditMode && canUpdateAnnouncement) || (!isEditMode && canCreateAnnouncement)) && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg overflow-hidden animate-in zoom-in-95 duration-200 max-h-[90vh] flex flex-col">
            <div className="flex justify-between items-center p-5 border-b border-slate-100 shrink-0">
              <h3 className="font-bold text-slate-800 text-lg">
                {isEditMode ? 'Editar Comunicado' : t('admin.ann.modal.title')}
              </h3>
              <button onClick={() => { setIsModalOpen(false); resetForm(); }}><X size={20} className="text-slate-400"/></button>
            </div>

            <form onSubmit={handleSave} className="p-6 space-y-4 overflow-y-auto custom-scrollbar">
              <Input
                label={t('admin.ann.modal.title_field')}
                value={title}
                onChange={e => setTitle(e.target.value)}
                placeholder="Ej. Mantenimiento de elevadores"
                required
              />

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">{t('admin.ann.modal.priority')}</label>
                <div className="grid grid-cols-2 gap-2">
                   {(Object.values(AnnouncementType) as AnnouncementType[]).map(p => (
                      <button
                        key={p}
                        type="button"
                        onClick={() => setType(p)}
                        className={`py-2 text-xs font-bold rounded-lg border transition-all ${
                          type === p
                          ? 'bg-primary-50 border-primary-500 text-primary-700'
                          : 'border-slate-200 text-slate-500 hover:bg-slate-50'
                        }`}
                      >
                        {t(`admin.ann.priority.${p.toLowerCase()}`)}
                      </button>
                   ))}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                 <Input
                   label={t('admin.ann.modal.expires')}
                   type="date"
                   value={expiresAt}
                   onChange={e => setExpiresAt(e.target.value)}
                 />
                 <div className="flex flex-col justify-end pb-1">
                   <label className={`cursor-pointer inline-flex items-center justify-center px-4 py-2 border border-slate-300 shadow-sm text-sm font-medium rounded-lg text-slate-700 bg-white hover:bg-slate-50 transition-colors w-full h-[42px] ${isUploading ? 'opacity-50 cursor-wait' : ''}`}>
                      {isUploading ? (
                        <Loader2 size={16} className="mr-2 text-slate-500 animate-spin" />
                      ) : (
                        <Paperclip size={16} className="mr-2 text-slate-500" />
                      )}
                      {isUploading ? 'Subiendo...' : attachmentUrl ? 'Adjunto OK' : t('admin.ann.modal.attach')}
                      <input type="file" className="hidden" disabled={isUploading} onChange={async (e) => {
                         const file = e.target.files?.[0];
                         if (file) {
                           const result = await upload(file);
                           if (result) {
                             setAttachmentUrl(result.url);
                           }
                         }
                      }}/>
                   </label>
                 </div>
              </div>

              {hasSections && (
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">
                    Visibilidad
                  </label>
                  <SectionMultiSelect
                    value={sectionIds}
                    onChange={setSectionIds}
                    resourceLabel="comunicado"
                    idPrefix="announcement"
                  />
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">{t('admin.ann.modal.content')}</label>
                <textarea
                  className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary-100"
                  rows={4}
                  value={content}
                  onChange={e => setContent(e.target.value)}
                  placeholder="Escribe el mensaje aquí..."
                  required
                />
              </div>

              <div className="flex gap-3 pt-4">
                <Button type="button" variant="secondary" className="flex-1" onClick={() => { setIsModalOpen(false); resetForm(); }}>
                  {t('common.close')}
                </Button>
                <Button type="submit" variant="primary" className="flex-1" icon={<Megaphone size={16}/>}>
                  {isEditMode ? 'Guardar cambios' : 'Publicar'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal de Vista Previa */}
      {isPreviewOpen && previewAnnouncement && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg overflow-hidden animate-in zoom-in-95 duration-200 max-h-[90vh] flex flex-col">
            <div className="flex justify-between items-center p-5 border-b border-slate-100 shrink-0">
              <h3 className="font-bold text-slate-800 text-lg">Vista Previa</h3>
              <button onClick={() => { setIsPreviewOpen(false); setPreviewAnnouncement(null); }}>
                <X size={20} className="text-slate-400"/>
              </button>
            </div>

            <div className="p-6 space-y-4 overflow-y-auto custom-scrollbar">
              {/* Badge de estado y tipo */}
              <div className="flex items-center gap-2 flex-wrap">
                {getStatusBadge(previewAnnouncement.isPublished)}
                {getPriorityBadge(previewAnnouncement.type)}
              </div>

              {/* Título */}
              <h2 className="text-xl font-bold text-slate-800">{previewAnnouncement.title}</h2>

              {/* Fecha */}
              <div className="flex items-center gap-4 text-sm text-slate-500">
                <span className="flex items-center gap-1">
                  <Calendar size={14} /> Publicado: {formatDate(previewAnnouncement.date)}
                </span>
                {previewAnnouncement.expiresAt && (
                  <span className="flex items-center gap-1 text-amber-600">
                    <Clock size={14} /> Expira: {formatDate(previewAnnouncement.expiresAt)}
                  </span>
                )}
              </div>

              {/* Contenido */}
              <div className="prose prose-sm max-w-none">
                <p className="text-slate-700 whitespace-pre-wrap">{previewAnnouncement.content}</p>
              </div>

              {/* Adjunto */}
              {previewAnnouncement.attachmentUrl && (
                <button
                  onClick={() => {
                    if (previewAnnouncement.attachmentFileId) {
                      openFile(previewAnnouncement.attachmentFileId, 'Adjunto', getMimeTypeFromUrl(previewAnnouncement.attachmentUrl || ''));
                    } else if (previewAnnouncement.attachmentUrl) {
                      openFile(previewAnnouncement.attachmentUrl, 'Adjunto', getMimeTypeFromUrl(previewAnnouncement.attachmentUrl));
                    }
                  }}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200 transition-colors text-sm font-medium"
                >
                  <Paperclip size={16} /> Ver adjunto
                </button>
              )}

              {/* Poll preview si existe */}
              {previewAnnouncement.poll && (
                <div className="p-4 bg-slate-50 rounded-lg border border-slate-200">
                  <h4 className="font-medium text-slate-800 mb-2 flex items-center gap-2">
                    <BarChart2 size={16} /> Encuesta incluida
                  </h4>
                  <p className="text-sm text-slate-600">{previewAnnouncement.poll.title}</p>
                  <p className="text-xs text-slate-500 mt-1">
                    {previewAnnouncement.poll.options.length} opciones
                  </p>
                </div>
              )}

              <div className="flex gap-3 pt-4">
                <Button
                  type="button"
                  variant="secondary"
                  className="flex-1"
                  onClick={() => { setIsPreviewOpen(false); setPreviewAnnouncement(null); }}
                >
                  Cerrar
                </Button>
                {canUpdateAnnouncement && (
                  <Button
                    type="button"
                    variant="primary"
                    className="flex-1"
                    icon={<Pencil size={16}/>}
                    onClick={() => {
                      setIsPreviewOpen(false);
                      openEditModal(previewAnnouncement);
                    }}
                  >
                    Editar
                  </Button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};
